import CustomListClient from './CustomListClient';
import HttpClient from '../../gateway/HttpClient';
import {notBlank, notUndefined} from '../../util/Assert';
import HttpMethod from '../../constant/HttpMethod';
import {CustomListMethods} from '../../constant/MethodNames';
import {
  CustomListBulkRecordType,
  CustomListCreateRequest,
  CustomListModel,
  CustomListRecordDeleteAllRequest,
  CustomListRecordExportRequest,
  CustomListRecordItem,
  CustomListRecordSearchRequest,
  CustomListRecordSearchResult,
} from '../../domain/custom-list/CustomListDomains';
import {AxiosRequestConfig} from 'axios';

class NestCustomListClient implements CustomListClient {
  private readonly httpClient: HttpClient;

  public constructor(httpClient: HttpClient) {
    notUndefined(httpClient, 'http client can not be undefined');
    this.httpClient = httpClient;
  }

  public create(body: CustomListCreateRequest): Promise<CustomListModel> {
    notUndefined(body, 'Body cannot be undefined');
    notBlank(body.id, 'Id cannot be blank');
    notBlank(body.name, 'Name cannot be blank');
    return this.httpClient.execute(HttpMethod.POST, CustomListMethods.CREATE, body);
  }

  public getAll(): Promise<CustomListModel[]> {
    return this.httpClient.execute(HttpMethod.GET, CustomListMethods.GET_ALL);
  }

  public getById(listId: string): Promise<CustomListModel> {
    notBlank(listId, 'List id cannot be blank');
    return this.httpClient.execute(HttpMethod.GET, `${CustomListMethods.GET_BY_ID}/${listId}`);
  }

  public findListsByRecordId(recordId: string): Promise<CustomListModel[]> {
    notBlank(recordId, 'Record id cannot be blank');
    return this.httpClient.execute(HttpMethod.GET, `${CustomListMethods.GET_LISTS_BY_RECORD}/${recordId}`);
  }

  public delete(listId: string): Promise<void> {
    notBlank(listId, 'List id cannot be blank');
    return this.httpClient.execute(HttpMethod.DELETE, `${CustomListMethods.DELETE}/${listId}`);
  }

  public deleteRecord(listId: string, recordId: string): Promise<void> {
    notBlank(listId, 'List id cannot be blank');
    notBlank(recordId, 'Record id cannot be blank');
    return this.httpClient.execute(
      HttpMethod.DELETE,
      `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/record/delete/${recordId}`,
    );
  }

  public deleteRecords(listId: string, body: CustomListRecordDeleteAllRequest): Promise<{deleted: number}> {
    notBlank(listId, 'List id cannot be blank');
    notUndefined(body, 'Body cannot be undefined');
    return this.httpClient.execute(
      HttpMethod.DELETE,
      `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/record/delete-all`,
      body,
    );
  }

  public addBulk(listId: string, type: CustomListBulkRecordType, file: File): Promise<void> {
    notBlank(listId, 'List id cannot be blank');
    notUndefined(type, 'Type cannot be undefined');
    notUndefined(file, 'File cannot be undefined');
    const formData = new FormData();
    formData.append('file', file);
    const endpoint: string = this.resolveBulkEndpoint(listId, type);
    return this.httpClient.execute(HttpMethod.POST, endpoint, formData);
  }

  public searchRecords(listId: string, body: CustomListRecordSearchRequest): Promise<CustomListRecordSearchResult> {
    notBlank(listId, 'List id cannot be blank');
    notUndefined(body, 'Body cannot be undefined');
    return this.httpClient.execute(
      HttpMethod.POST,
      `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/record/search`,
      body,
    );
  }

  public countAllRecords(listId: string): Promise<{total: number}> {
    notBlank(listId, 'List id cannot be blank');
    return this.httpClient.execute(
      HttpMethod.GET,
      `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/record/count`,
    );
  }

  public getRecordById(listId: string, recordId: string): Promise<CustomListRecordItem> {
    notBlank(listId, 'List id cannot be blank');
    notBlank(recordId, 'Record id cannot be blank');
    return this.httpClient.execute(
      HttpMethod.GET,
      `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/record/${recordId}`,
    );
  }

  public exportRecords(listId: string, body: CustomListRecordExportRequest): Promise<Blob> {
    notBlank(listId, 'List id cannot be blank');
    notUndefined(body, 'Body cannot be undefined');
    const req: AxiosRequestConfig = {
      url: `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/record/export`,
      method: 'POST',
      data: body,
      responseType: 'blob',
    };
    return this.httpClient.executeWithRequest<{data: Blob}>(req).then((response) => response.data);
  }

  private resolveBulkEndpoint(listId: string, type: CustomListBulkRecordType): string {
    switch (type) {
      case 'Entity':
        return `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/entity/bulk`;
      case 'Individual':
        return `${CustomListMethods.GET_RECORDS_PREFIX}/${listId}/individual/bulk`;
      default:
        throw new Error('Invalid custom list type');
    }
  }
}

export default NestCustomListClient;
